<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { reactive } from 'vue'

import {
  definePageMeta,
  navigateTo,
  useRoute,
  useHead,
} from '#imports'
import { useTempStore } from '@/stores/temp'
import AButton from '@/components/atoms/AButton/index.vue'
import ALabelWithBoxedRequired from '@/components/atoms/ALabelWithBoxedRequired/index.vue'
import { resetPassword } from '@/composables/api/customer/Auth/index'
import { trackPVResetPassword } from '@/utils/tracking/eventTracking'
import { metaTitleFormat } from '@/utils/meta'

/**
 * Initializations
 */
trackPVResetPassword()

definePageMeta({
  layout: 'login',
})

const route = useRoute()
const { t } = useI18n()
useHead({
  titleTemplate: (titleChunk) => {
    return metaTitleFormat(titleChunk, t('forgot_password.title'))
  },
})
const formData = reactive({
  email: null,
})
const formstatus = reactive({
  valid: false,
  isSubmitting: false,
  errorMessages: null,
})
const rules = {
  emailRules: [
    // @ts-expect-error TS7006
    v => !!v || t('forgot_password.required'),
    // @ts-expect-error TS7006
    v => /.+@.+/.test(v) || t('forgot_password.incorrect_email_address'),
  ],
}
const submitForm = async () => {
  if (formstatus.valid) {
    formstatus.errorMessages = null // clear error messages
    const tempStore = useTempStore()
    // @ts-expect-error TS2345
    tempStore.setForgotPasswordEmail(formData.email) // store submitted password in temp store
    formstatus.isSubmitting = true
    const response = await resetPassword(formData.email!)
    if (response.status.value === 'success') {
      navigateTo('/mypage/resetpasswordconfirm')
    }
    else {
      formstatus.errorMessages = response.error?.value?.data?.message
    }
    formstatus.isSubmitting = false
  }
  else {
    alert(t('forgot_password.double_check_text'))
    formstatus.valid = false
  }
}
const onSubmit = () => {
  if (!formstatus.valid) {
    return
  }
  submitForm()
}

const loginPath = computed(() => {
  const overrideLoginPath = [route.query.overrideLoginPath].flat()[0] || ''
  return overrideLoginPath || '/mypage/login'
})
</script>

<template>
  <div class="forgot-password-reset">
    <v-sheet
      elevation="0"
      rounded="xl"
      class="sheet-container"
    >
      <h1 class="title">
        {{ $t('forgot_password.forgot_password') }}
      </h1>
      <p class="description">
        {{ $t('forgot_password.password_reset_text') }}
      </p>
      <v-form
        v-model="formstatus.valid"
        class="form-container"
        @submit.prevent="onSubmit"
      >
        <ALabelWithBoxedRequired name="input">
          <template #text>
            {{ $t('forgot_password.input_label') }}
          </template>
          <v-text-field
            v-model="formData.email"
            :placeholder="$t('forgot_password.mail_address')"
            :rules="rules.emailRules"
            type="email"
            required
          />
        </ALabelWithBoxedRequired>
        <AButton
          color="primary"
          size="large"
          :disabled="formstatus.valid === false"
          :loading="formstatus.isSubmitting"
          @click="submitForm()"
        >
          {{ $t('forgot_password.submit') }}
        </AButton>
        <div
          class="back-login-link"
          :class="{ wide: $t('common.sentence.back_to_login').length > 15 }"
        >
          <nuxt-link
            :to="loginPath"
            class="arrow-link"
          >
            {{ $t('common.sentence.back_to_login') }}
          </nuxt-link>
        </div>
        <span
          v-if="formstatus.errorMessages"
          class="text-error"
        >{{ formstatus.errorMessages }}</span>
      </v-form>
    </v-sheet>
  </div>
</template>

<style lang="scss" scoped>
.forgot-password-reset {
  .sheet-container {
    padding: 56px 40px 12px;
    text-align: center;
  }

  .title {
    margin-bottom: 24px;
  }

  .description {
    margin-bottom: 28px;
    text-align: left;
    white-space: pre-wrap;
  }

  .form-container {
    width: 90%;
    max-width: 230px;
    margin: 0 auto;
  }

  .a-button {
    display: block;
    width: 172px;
    margin: 0 auto 12px;
    font-weight: bold;
  }

  .back-login-link {
    max-width: 100px;
    margin: 0 auto;
    margin-bottom: 16px;
  }

  .back-login-link.wide {
    max-width: 140px;
  }
}

:deep(.v-text-field .v-input__control) {
  font-family: Roboto, sans-serif;

  input {
    font-size: 12px;
  }
}
</style>
